/**
 * Basic cookie utility functions
 */

import type { CookieOptions, DeleteCookieOptions } from './types'

/**
 * Converts all cookies into a key-value object
 * @returns Object containing all cookies as key-value pairs
 */
export const cookieAsObject = (): Record<string, string> => {
  if (typeof window === 'undefined') {
    return {}
  }

  return window?.document?.cookie
    .split(';')
    .map(x => x.trim())
    .map(x => {
      // Use only the first '=' as the split point
      const splitOn = x.indexOf('=')
      return [x.slice(0, splitOn), x.slice(splitOn + 1)]
    })
    .reduce(
      (acc, [k, v]) => {
        acc[k] = v
        return acc
      },
      {} as Record<string, string>,
    )
}

/**
 * Returns EVERY value the browser serializes for a cookie name, in browser
 * order. Same-named cookies at different scopes (e.g. `.suitsupply.com` vs a
 * subdomain) each appear once — document.cookie exposes no domain metadata,
 * so the caller cannot tell which is which, only that a collision exists.
 */
export const getAllCookieValues = (name: string): string[] => {
  if (typeof window === 'undefined') {
    return []
  }
  return window.document.cookie
    .split(';')
    .map(part => part.trim())
    .filter(part => part.startsWith(`${name}=`))
    .map(part => part.slice(name.length + 1))
}

const warnedDuplicateNames = new Set<string>()

/**
 * Gets a specific cookie by name — the domain-aware reader (FC-183 §5, O5).
 *
 * When a name resolves to more than one distinct value, the FIRST serialized
 * value wins (browsers order by most-specific path, then earliest creation —
 * for our stack that is the `.suitsupply.com`-scoped cookie written by the
 * shared client) and a diagnostic is emitted once per name, because duplicate
 * same-named cookies are exactly how split identities and orphaned
 * conversions start.
 * @param name - The name of the cookie to retrieve
 * @returns The cookie value or undefined if not found
 */
export const getCookie = (name: string): string | undefined => {
  const values = getAllCookieValues(name)
  if (values.length === 0) {
    return undefined
  }
  if (new Set(values).size > 1 && !warnedDuplicateNames.has(name)) {
    warnedDuplicateNames.add(name)
    console.warn(
      `[@susu/cookie] "${name}" resolves to ${values.length} distinct cookies — same name at different scopes. Using the first; consolidate the writers (FC-183 §5).`,
    )
  }
  return values[0]
}

/**
 * Applies one raw `name=value; attributes` cookie string to document.cookie.
 * The sanctioned low-level escape hatch for cookies minted elsewhere (e.g.
 * server-provided strings applied client-side) — application code must never
 * touch document.cookie itself (FC-183 §5, O5).
 */
export const applyRawCookieString = (cookieString: string): void => {
  if (typeof window === 'undefined') {
    return
  }
  window.document.cookie = cookieString
}

/**
 * Sets a cookie with the given name, value, and optional parameters
 * @param name - The name of the cookie
 * @param value - The value of the cookie
 * @param options - Optional cookie parameters (expires, path, domain, secure, sameSite)
 */
export const setCookie = (
  name: string,
  value: string,
  options: CookieOptions = {},
): void => {
  if (typeof window === 'undefined') {
    return
  }

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`

  if (options.expires) {
    const expires =
      options.expires instanceof Date
        ? options.expires
        : new Date(options.expires)
    cookieString += `; expires=${expires.toUTCString()}`
  }

  if (options.path) {
    cookieString += `; path=${options.path}`
  }

  if (options.domain) {
    cookieString += `; domain=${options.domain}`
  }

  if (options.secure) {
    cookieString += '; secure'
  }

  if (options.sameSite) {
    cookieString += `; samesite=${options.sameSite}`
  }

  document.cookie = cookieString
}

/**
 * Deletes a cookie by setting its expiration date to the past
 * @param name - The name of the cookie to delete
 * @param options - Optional cookie parameters (path, domain)
 */
export const deleteCookie = (
  name: string,
  options: DeleteCookieOptions = {},
): void => {
  setCookie(name, '', {
    ...options,
    expires: new Date(0),
  })
}
